import { ref, computed } from 'vue';

export function useCoursesFilter(courses, instructors) {
  const searchQuery = ref('');
  const sortKey = ref('title');
  const sortOrder = ref('asc');
  const filters = ref({
    status: '',
    category: '',
    instructor: '',
    type: ''
  });

  const filteredCourses = computed(() => {
    let filtered = courses.value.filter(course => {
      // Create a more robust instructor name search
      let instructorFullName = '';
      
      // Try to get instructor name from the course.instructor object
      if (course.instructor) {
        const firstName = course.instructor.first_name || '';
        const lastName = course.instructor.last_name || '';
        instructorFullName = `${firstName} ${lastName}`.trim().toLowerCase();
      }
      
      // If we couldn't get it from course.instructor, try to find it in the instructors array
      if ((!instructorFullName || instructorFullName === '') && course.instructor_id) {
        const instructorId = normalizeId(course.instructor_id);
        const instructor = instructors.value.find(i => normalizeId(i.id) === instructorId);
        if (instructor) {
          const firstName = instructor.first_name || '';
          const lastName = instructor.last_name || '';
          instructorFullName = `${firstName} ${lastName}`.trim().toLowerCase();
        }
      }
      
      const searchTerm = searchQuery.value.toLowerCase().trim();
      
      // More thorough search matching
      const titleMatches = course.title ? course.title.toLowerCase().includes(searchTerm) : false;
      const titleArMatches = course.title_ar ? course.title_ar.toLowerCase().includes(searchTerm) : false;
      const instructorMatches = instructorFullName && searchTerm ? instructorFullName.includes(searchTerm) : false;
      
      const matchesSearch = 
        titleMatches || 
        titleArMatches || 
        instructorMatches;
      
      const matchesStatus = !filters.value.status || course.status === filters.value.status;
      const matchesCategory = !filters.value.category || normalizeId(course.category_id) === normalizeId(filters.value.category);
      
      let matchesInstructor = true;
      if (filters.value.instructor) {
        const filterInstructorId = normalizeId(filters.value.instructor);
        const courseInstructorId = normalizeId(course.instructor_id);
        matchesInstructor = courseInstructorId === filterInstructorId;
      }
      
      const matchesType = !filters.value.type || 
                         (filters.value.type === 'premium' ? course.is_premium : !course.is_premium);

      return matchesSearch && matchesStatus && matchesCategory && matchesInstructor && matchesType;
    });

    // Sorting logic
    return filtered.sort((a, b) => {
      let modifier = 1;
      if (sortOrder.value === 'desc') modifier = -1;
      
      if (sortKey.value === 'title') return a.title.localeCompare(b.title) * modifier;
      if (sortKey.value === 'category') return (a.category?.title || '').localeCompare(b.category?.title || '') * modifier;
      if (sortKey.value === 'instructor') {
        const aName = `${a.instructor?.first_name || ''} ${a.instructor?.last_name || ''}`.trim();
        const bName = `${b.instructor?.first_name || ''} ${b.instructor?.last_name || ''}`.trim();
        return aName.localeCompare(bName) * modifier;
      }
      if (sortKey.value === 'minutes') return (a.minutes - b.minutes) * modifier;
      if (sortKey.value === 'status') return a.status.localeCompare(b.status) * modifier;
      if (sortKey.value === 'is_premium') return (a.is_premium === b.is_premium ? 0 : a.is_premium ? -1 : 1) * modifier;
      if (sortKey.value === 'students') return (a.students?.length - b.students?.length) * modifier;
      
      return 0;
    });
  });

  function updateFilters(newFilters) {
    filters.value = newFilters;
  }

  function updateSearch(newSearch) {
    searchQuery.value = newSearch;
  }

  function sortBy(key) {
    if (sortKey.value === key) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
      sortKey.value = key;
      sortOrder.value = 'asc';
    }
  }

  function normalizeId(id) {
    // Convert to string, trim whitespace, and handle null/undefined
    return id ? String(id).trim() : '';
  }

  return {
    searchQuery,
    filters,
    filteredCourses,
    updateFilters,
    updateSearch,
    sortBy,
    normalizeId
  };
} 